import map from 'ramda/src/map';
import reject from 'ramda/src/reject';
import filter from 'ramda/src/filter';
import pipe from 'ramda/src/pipe';
import sortBy from 'ramda/src/sortBy';
import prop from 'ramda/src/prop';
import dissoc from 'ramda/src/dissoc';
import flatten from 'ramda/src/flatten';
import reverse from 'ramda/src/reverse';
import reduce from 'ramda/src/reduce';
import find from 'ramda/src/find';
import contains from 'ramda/src/contains';
import groupWith from 'ramda/src/groupWith';
import pick from 'ramda/src/pick';
import pathOr from 'ramda/src/pathOr';
import { createSelector } from 'reselect';
import {
  getCollection,
} from '../../barbers/selector';
import {
  getPaymentFilters,
  getStatusFilters,
  isFilterActive,
  filterByBarber,
  getActiveBrandIds,
  getSalesBarbersFilter,
} from '../selectors';
import { getBrands } from '../../../products/brands/selectors';

const removeSalesWithoutProducts = reject(item => item.products.length === 0);
export const filterNotPaidSales = reject(item => !item.chargeId && item.paymentType === 'reader');

const filterSales = pipe(
  removeSalesWithoutProducts,
  filterNotPaidSales
);

const getProducts = state => state.reports.products;

const filterByPayment = (paymentFilters) => {
  const posFilter = isFilterActive('POS')(paymentFilters);
  const cashFilter = isFilterActive('Cash/Other')(paymentFilters);

  return filter(item =>
    (posFilter && (item.paymentType === 'reader' || item.paymentType === 'card'))
    || (cashFilter && item.paymentType === 'cash')
  );
};

const filterByStatus = (statusFilters) => {
  return (items) => {
    const refundedFilter = isFilterActive('Refunded')(statusFilters);
    const paidFilter = isFilterActive('Paid')(statusFilters);
    return filter(item => (refundedFilter && item.chargeRefundId) || (paidFilter && !item.chargeRefundId), items);
  };
};

const makeProductsReport = (products, barbers, paymentFilters, statusFilters) => {
  const byPayment = filterByPayment(paymentFilters);
  const byStatus = filterByStatus(statusFilters);
  const applyFilters = pipe(filterSales, byPayment, byStatus);

  return map(item => ({
    ...item,
    status: item.chargeRefundId ? 'Refunded' : 'Paid',
  }), applyFilters(products));
};

export const getProductsReport = createSelector(
  getProducts,
  getCollection,
  getPaymentFilters,
  getStatusFilters,
  makeProductsReport,
);

export const getProductsReportWithBarberFilter = createSelector(
  getSalesBarbersFilter,
  getProductsReport,
  filterByBarber,
);

const productBrandId = pathOr('unbranded', ['product', 'productBrandId']);

const filterByBrand = (brandFilters, data) => {
  return filter(item => find(
    product => contains(productBrandId(product), brandFilters) || productBrandId(product) === null,
    item.products
  ), data);
};

export const getProductsReportWithFilters = createSelector(
  getActiveBrandIds,
  getProductsReportWithBarberFilter,
  filterByBrand
);

const onlyPaid = filter(item => item.status === 'Paid');
const extractProductsFromSales = pipe(map(item => item.products), flatten);
const orderByProductId = sortBy(prop('productId'));
const groupByProductId = groupWith((item1, item2) => item1.productId === item2.productId);
const orderByQuantityDesc = pipe(sortBy(prop('#')), reverse);
const sumProducts = map(reduce((acc, item) => ({
  ...acc,
  Name: item.name,
  '#': acc['#'] + item.quantity,
  brandId: productBrandId(item),
  SKU: item.product.sku,
}), {
  '#': 0,
}));

const applyBrandName = brands => map((item) => {
  return dissoc('brandId', {
    ...item,
    Brand: brands[item.brandId] && brands[item.brandId].name,
  });
});

const applyPercentage = (products) => {
  const totalSold = reduce((acc, item) => acc + item['#'], 0, products);
  return map(item => ({
    ...item,
    '%': Math.round((item['#'] / totalSold) * 100),
  }), products);
};

const reorderColumns = map(pick(['SKU', 'Brand', 'Name', '#', '%']));

const makeProductTable = (report, brands) => {
  return pipe(
    onlyPaid,
    extractProductsFromSales,
    orderByProductId,
    groupByProductId,
    sumProducts,
    orderByQuantityDesc,
    applyBrandName(brands),
    applyPercentage,
    reorderColumns,
  )(report);
};

export const getProductsTable = createSelector(
  getProductsReportWithBarberFilter,
  getBrands,
  makeProductTable,
);
